"use client";

import { createContext, use, useState } from "react";
import { useDeliveryLocation } from "@/hooks/checkout/useDeliveryLocation";
import {
  defaultDeliveryWindow,
  type DeliveryWindow,
} from "@/lib/checkout/delivery-window";
import {
  getInitialListingQuantity,
  normalizeListingQuantity,
} from "./listing-action-state";
import { useListingQuery } from "./listing-query";

interface ListingBuyState {
  quantity: number;
  setQuantity: (quantity: number) => void;
  quantityPending: boolean;
  setQuantityPending: (pending: boolean) => void;
  minQuantity: number;
  checkoutMinimum: number;
  deliveryWindow: DeliveryWindow;
  setDeliveryWindow: (window: DeliveryWindow) => void;
  hasDockOnSite: boolean;
  setHasDockOnSite: (hasDock: boolean) => void;
  leadTimeDays: number;
  delivery: ReturnType<typeof useDeliveryLocation>;
  addressDialog: ListingAddressDialogIntent | null;
  openAddressDialog: (intent: ListingAddressDialogIntent) => void;
  closeAddressDialog: () => void;
}

export type ListingAddressDialogIntent = "edit" | "buy";

export const LISTING_DELIVERY_ADDRESS_INPUT_ID = "listing-delivery-address";

const ListingBuyStateContext = createContext<ListingBuyState | null>(null);
const MIN_LISTING_QUANTITY = 1;

interface ListingBuyStateProviderProps {
  checkoutMinimum: number;
  suggestedQuantity: number;
  leadTimeDays: number;
  children: React.ReactNode;
}

export function ListingBuyStateProvider({
  checkoutMinimum,
  suggestedQuantity,
  leadTimeDays,
  children,
}: ListingBuyStateProviderProps) {
  const minQuantity = MIN_LISTING_QUANTITY;
  const [quantity, setQuantity] = useRequestedQuantity(suggestedQuantity);
  const [deliveryWindow, setDeliveryWindow] = useState(() =>
    defaultDeliveryWindow(leadTimeDays),
  );
  const [quantityPending, setQuantityPending] = useState(false);
  const [hasDockOnSite, setHasDockOnSite] = useState(true);
  const [addressDialog, setAddressDialog] =
    useState<ListingAddressDialogIntent | null>(null);
  const delivery = useDeliveryLocation();

  const value: ListingBuyState = {
    quantity,
    setQuantity: (next: number) => setQuantity(normalizeListingQuantity(next)),
    quantityPending,
    setQuantityPending,
    minQuantity,
    checkoutMinimum,
    deliveryWindow,
    setDeliveryWindow,
    hasDockOnSite,
    setHasDockOnSite,
    leadTimeDays,
    delivery,
    addressDialog,
    openAddressDialog: setAddressDialog,
    closeAddressDialog: () => setAddressDialog(null),
  };

  return (
    <ListingBuyStateContext value={value}>
      {children}
    </ListingBuyStateContext>
  );
}

export function useListingBuyState(): ListingBuyState {
  const state = use(ListingBuyStateContext);
  if (!state) {
    throw new Error(
      "useListingBuyState must be used inside a ListingBuyStateProvider",
    );
  }
  return state;
}

function useRequestedQuantity(suggestedQuantity: number) {
  const searchParams = useListingQuery();
  return useState(() =>
    getInitialListingQuantity(
      searchParams.get("qty") ?? undefined,
      suggestedQuantity,
    ),
  );
}
